import { Link } from '@tanstack/react-router'
import { useState } from 'react'

import SkeletonGroup from '@/components/motion/SkeletonGroup'
import Change from '@/components/ui/Change'
import Money from '@/components/ui/Money'
import SectionHead from '@/components/ui/SectionHead'
import SeeAllLink from '@/components/ui/SeeAllLink'
import Skeleton from '@/components/ui/Skeleton'
import { useCompanyAnnouncements, useCompanySentiment } from '@/features/company/api/profileQueries'
import { useSectorPeers, type SectorPeer } from '@/features/company/api/sectorPeers'
import MoodGauge from '@/features/market-rail/components/MoodGauge'
import RailNewsRow, { RailNewsRowSkeleton } from '@/features/market-rail/components/RailNewsRow'
import RailRows from '@/features/market-rail/components/RailRows'
import { useRailBlock } from '@/features/market-rail/useRailFit'
import { cn } from '@/lib/utils'
import type { CompanySentiment } from '@/schemas/company-profile'
import CompanyMoodExplainer from '@/screens/stock/components/overview/CompanyMoodExplainer'
import {
  NOTICE_PAGE_SIZE,
  RAIL_NOTICE_ROWS,
  noticeMetaParts,
  noticeTab,
} from '@/screens/stock/components/overview/notices'
import { sentimentArt, sentimentBand } from '@/screens/stock/components/overview/sentimentBand'

/** "People also look at": the mock's four rows. */
const PEER_ROWS = 4

/** The news block's skeleton rows, trimmed by the rail's fit like the notices they stand in for. */
const NOTICE_SKELETON_ROWS = Array.from({ length: RAIL_NOTICE_ROWS }, (_, index) => (
  <RailNewsRowSkeleton key={index} />
))

/**
 * "News about {symbol}": the company's latest notices — the same page
 * Overview's own "News about {symbol}" reads, so on that tab the two blocks
 * are one request.
 *
 * Omitted, not an empty state, when there are none or the read failed: the
 * rail has other blocks, and a block that says "nothing about NABIL" on every
 * quiet stock is noise. The skeleton holds the space only while the answer is
 * still out, so the rail does not jump once it lands.
 *
 * Each row opens the tab its notice belongs to (`noticeTab`: a dividend notice
 * the Dividend tab, a results notice Reports, an AGM or book closure Events,
 * anything else Announcements), as the mock's rows do; "More" opens
 * Announcements. With no `params` the router keeps the page's own symbol: this
 * rail is only ever drawn on that page.
 */
function CompanyNewsBlock({ symbol }: { symbol: string }) {
  const block = useRailBlock('company-news')
  const news = useCompanyAnnouncements(symbol, 1, NOTICE_PAGE_SIZE)

  if (news.isPending) {
    return (
      <SkeletonGroup aria-hidden="true" {...block}>
        {/* The 14px section title's 20px line. */}
        <div className="flex h-5 items-center">
          <Skeleton className="h-4 w-40 rounded-xs" />
        </div>
        <div className="mt-3">
          <RailRows block="company-news" rows={NOTICE_SKELETON_ROWS} />
        </div>
      </SkeletonGroup>
    )
  }
  if (news.data === undefined || news.data.rows.length === 0) return null

  return (
    <section aria-label={`News about ${symbol}`} {...block}>
      <SectionHead
        size="sm"
        title={`News about ${symbol}`}
        action={
          <SeeAllLink
            to="/stocks/$symbol"
            search={{ tab: 'announcements' }}
            size="rail"
            aria-label={`More news about ${symbol}`}
          >
            More
          </SeeAllLink>
        }
      />
      <div className="mt-3">
        <RailRows
          block="company-news"
          rows={news.data.rows.slice(0, RAIL_NOTICE_ROWS).map((notice, index) => (
            // One fixed page, never reordered, and nothing on a notice is
            // unique: two can share a date and a title.
            <RailNewsRow
              key={index}
              meta={noticeMetaParts(notice)}
              title={notice.title}
              link={{ to: '/stocks/$symbol', search: { tab: noticeTab(notice) } }}
            />
          ))}
        />
      </div>
    </section>
  )
}

/** What the reading and the track do while the reveal shows (mock `.greed-mood`): fade out. */
const MOOD_AWAY =
  'transition-opacity duration-fast ease-out group-hover/mood:opacity-0 group-focus-visible/mood:opacity-0'

/**
 * The loaded reading as the mock's rail draws it (`greed-mood`, the market rail's `MoodButton`):
 * one button over the score, its word and the five-band track. Pointing at it (or focusing it)
 * fades those out and brings up Mitra in the band's pose over "25 · Cautious" and the line,
 * rising 4px; pressing it opens the company's explainer.
 */
function CompanyMoodButton({ symbol, reading }: { symbol: string; reading: CompanySentiment }) {
  const [open, setOpen] = useState(false)
  const band = sentimentBand(reading.label)
  const score = Math.round(reading.score)
  const word = band?.word ?? 'No verdict'

  return (
    <>
      <button
        type="button"
        aria-haspopup="dialog"
        aria-label={`${symbol} fear and greed ${String(score)}, ${word}. Open the explainer.`}
        onClick={() => {
          setOpen(true)
        }}
        className="group/mood relative mt-3 block w-full cursor-pointer text-left outline-none focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-ring"
      >
        <span className={cn('flex items-baseline justify-between gap-3', MOOD_AWAY)}>
          <span className="tnum text-h-m">{score}</span>
          {band === null ? null : (
            <span className="text-body-s text-muted-foreground">{band.word}</span>
          )}
        </span>
        <MoodGauge
          score={reading.score}
          label={band?.word ?? 'no verdict'}
          subject={`${symbol} fear and greed`}
          className={cn('mt-3', MOOD_AWAY)}
        />
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 flex translate-y-1 items-center gap-2 opacity-0 transition-[opacity,translate] duration-med ease-out group-hover/mood:translate-y-0 group-hover/mood:opacity-100 group-focus-visible/mood:translate-y-0 group-focus-visible/mood:opacity-100"
        >
          <img
            src={sentimentArt(band)}
            alt=""
            width={36}
            height={44}
            className="h-11 w-9 shrink-0 object-contain"
          />
          <span className="flex min-w-0 flex-1 flex-col">
            <span className="truncate text-h-s text-foreground">
              {score} · {word}
            </span>
            <span className="truncate text-body-xs text-muted-foreground">
              What this reading means
            </span>
          </span>
          <span className="shrink-0 text-h-m text-muted-foreground">›</span>
        </span>
      </button>
      <CompanyMoodExplainer symbol={symbol} reading={reading} open={open} onOpenChange={setOpen} />
    </>
  )
}

/**
 * The company's fear-and-greed reading, first in the rail as the mock orders it, on the same
 * `MoodGauge` the market rail draws, and like the market rail's a button that reveals Mitra on
 * hover and opens an explainer (`CompanyMoodButton`).
 *
 * The skeleton holds the block's place while the reading is out, so the rail's fit, priced on
 * it, does not shift when it lands. Omitted when there is no reading (`data: null`) or the read
 * failed.
 */
function CompanyMoodBlock({ symbol }: { symbol: string }) {
  const block = useRailBlock('fear-greed')
  const sentiment = useCompanySentiment(symbol)

  if (sentiment.isPending) {
    return (
      <SkeletonGroup aria-hidden="true" {...block}>
        <div className="flex h-5 items-center">
          <Skeleton className="h-4 w-32 rounded-xs" />
        </div>
        <div className="mt-3 flex h-6 items-center justify-between gap-3">
          <Skeleton className="h-5 w-9 rounded-xs" />
          <Skeleton className="h-4 w-16 rounded-xs" />
        </div>
        <Skeleton className="mt-3 h-2 w-full rounded-xs" />
      </SkeletonGroup>
    )
  }
  const reading = sentiment.data
  if (reading === undefined || reading === null) return null

  return (
    <section aria-label="Fear and greed" {...block}>
      <SectionHead size="sm" title="Fear and greed" />
      <CompanyMoodButton symbol={symbol} reading={reading} />
    </section>
  )
}

/**
 * One peer row (mock `.mrail` peer): symbol 14/600, last price in the secondary grey, the move in
 * its own right-aligned column. The whole row opens Compare with that stock beside this one, and
 * takes the rail's wash (`rail-row-wash`) with no change of ink, as the mock's does.
 */
function PeerRow({ symbol, peer }: { symbol: string; peer: SectorPeer }) {
  return (
    <Link
      to="/stocks/$symbol"
      params={{ symbol }}
      search={{ tab: 'compare', with: [peer.symbol] }}
      aria-label={`Compare with ${peer.symbol}`}
      className="flex items-center justify-between gap-2 border-b border-border-subtle py-2 text-body-m text-muted-foreground rail-row-wash last:border-b-0 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring"
    >
      <span className="truncate font-semibold text-foreground">{peer.symbol}</span>
      <span className="flex shrink-0 items-center gap-4">
        {peer.ltp === null ? null : <Money value={peer.ltp} />}
        <Change value={peer.changePercent} percent className="w-16 justify-end" />
      </span>
    </Link>
  )
}

/** A peer row's loading shape: one 20px line at `py-2`. */
function PeerRowSkeleton() {
  return (
    <div className="flex items-center justify-between gap-2 py-2">
      <Skeleton className="h-4 w-14 rounded-xs" />
      <div className="flex shrink-0 items-center gap-4">
        <Skeleton className="h-4 w-14 rounded-xs" />
        <Skeleton className="h-4 w-12 rounded-xs" />
      </div>
    </div>
  )
}

const PEER_SKELETON_ROWS = Array.from({ length: PEER_ROWS }, (_, index) => (
  <PeerRowSkeleton key={index} />
))

/**
 * The mock's "People also look at": four stocks, each opening Compare. mm-be keeps no record of
 * what members look at together (`asks/cp1.md` A2), so these are the owner's proxy (ruling C3):
 * the busiest stocks of the same sector by today's turnover (`useSectorPeers`). The heading says
 * that, rather than the mock's words, which would claim a reading nobody took.
 *
 * Omitted for a company with no sector on record, a sector with no other member, or a failed read.
 */
function PeersBlock({ symbol, sectorSymbol }: { symbol: string; sectorSymbol: string | null }) {
  const block = useRailBlock('stocks')
  const peers = useSectorPeers(symbol, sectorSymbol, PEER_ROWS)
  if (sectorSymbol === null) return null

  if (peers.isPending) {
    return (
      <SkeletonGroup aria-hidden="true" {...block}>
        <div className="flex h-5 items-center">
          <Skeleton className="h-4 w-36 rounded-xs" />
        </div>
        <div className="mt-2">
          <RailRows block="stocks" rows={PEER_SKELETON_ROWS} />
        </div>
      </SkeletonGroup>
    )
  }
  if (peers.data === undefined || peers.data.length === 0) return null

  return (
    <section aria-label="Most traded in the sector" {...block}>
      <SectionHead size="sm" title="Most traded in the sector" />
      <div className="mt-2">
        <RailRows
          block="stocks"
          rows={peers.data.map((peer) => (
            <PeerRow key={peer.symbol} symbol={symbol} peer={peer} />
          ))}
        />
      </div>
    </section>
  )
}

/**
 * The Stock page's own rail, in the mock's order (cp1, 2026-10-29): the company's fear-and-greed
 * reading, News about {symbol}, then the sector's most-traded stocks in the mock's "People also
 * look at" slot (see `PeersBlock`). The ask bar that names the stock is the docked Ask Mitra bar's
 * page chip.
 *
 * `sectorSymbol` is the company's sector, once the company has loaded; the pending page has none,
 * and draws the peers block only once it does.
 *
 * `ScreenLayout` fits it like every rail: both lists trim to one row, then the reading, the peers
 * and finally the news leave (`RAIL_DROP_ORDER`).
 */
export default function StockRail({
  symbol,
  sectorSymbol = null,
}: {
  symbol: string
  sectorSymbol?: string | null
}) {
  return (
    <div className="flex flex-col gap-10">
      <CompanyMoodBlock symbol={symbol} />
      <CompanyNewsBlock symbol={symbol} />
      <PeersBlock symbol={symbol} sectorSymbol={sectorSymbol} />
    </div>
  )
}
